import { z } from 'zod';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { S2GroupedBarChartUnit } from 'libs/end-to-end/src/grouped-bar';
import { ExplanationsOutputDictionary } from 'libs/end-to-end/src/explanations';
import { TimeSeriesTrendsResult } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { TimeSeriesTrendsResultBucketElement } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { Colors } from 'libs/end-to-end/src/types/branding/color';
import { TimeSeriesTrendsResultBucket } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { zip } from 'lodash';
import { humanizeDateRange } from 'libs/end-to-end/src/humanize-date-range';
import { parseLocalDate } from 'libs/end-to-end/src/humanize-date-range';
import { createDefaultLineChartConfig } from 'libs/end-to-end/src/line-chart';
import { LineChartConfig } from 'libs/end-to-end/src/line-chart';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { DateDuration } from 'libs/end-to-end/src/dates';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { getStandardDateFormatPattern } from 'libs/end-to-end/src/dates';
import { compareAsc } from 'date-fns';
import { isWithinInterval } from 'date-fns';
import { parseISO } from 'date-fns';
import { getDateDuration } from 'libs/end-to-end/src/dates';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';
import { ViewByOption } from 'libs/end-to-end/src/view-by';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';

export const ProgressOutputFile = 'progress_output.json';
export const ProgressOutputFinalFile = 'heatmap_output_final.json';

export const ProgressOutputGroupLevel = z
  .object({
    level: z.string(),
    value: z.number().nullish(),
    numerator: z.number().nullish(),
    count: z.number().nullish(),
  })
  .describe('ProgressOutputGroupLevel');

export type ProgressOutputGroupLevel = z.infer<typeof ProgressOutputGroupLevel>;

export const ProgressOutputGroup = z
  .object({
    name: z.string(),
    levels: ProgressOutputGroupLevel.array(),
  })
  .describe('ProgressOutputGroup');

export type ProgressOutputGroup = z.infer<typeof ProgressOutputGroup>;

export const ProgressOutput = z
  .object({
    focusArea: FocusArea,
    metric: z.string(),
    dimension: z.string(),
    rateGroups: ProgressOutputGroup.array(),
    rawDisparityGroups: ProgressOutputGroup.array(),
    controlledDisparityGroups: ProgressOutputGroup.array(),
    isMetricBoolean: z.boolean(),
    explanations: ExplanationsOutputDictionary.optional(),
  })
  .describe('ProgressOutput');

export type ProgressOutput = z.infer<typeof ProgressOutput>;

export const DisparityField = z
  .enum(['rateGroups', 'rawDisparityGroups', 'controlledDisparityGroups'])
  .describe('DisparityField');

export const DisparityFields = Object.values(DisparityField.Values);

export const Disparities = Object.values(DisparityField.Values);

export type DisparityField = z.infer<typeof DisparityField>;

export const LevelValue = z.object({
  level: z.string(),
  value: z.number().nullable(),
  color: Color,
  count: z.number(),
  isCheckered: z.boolean().optional(),
  observedValue: z.number().optional(),
  numerator: z.number().nullish(),
});

export type LevelValue = z.infer<typeof LevelValue>;

export const ProgressYearProps = z.object({
  values: LevelValue.array(),
  year: z.string(),
});

export type ProgressYearProps = z.infer<typeof ProgressYearProps>;

export const ProgressBarChartSerializableProps = z.object({
  data: ProgressYearProps.array(),
  metricDisplayName: z.string(),
  whiteLabelingSubjectPlural: z.string(),
  units: S2GroupedBarChartUnit,
  comparisonGroupValue: z.string(),
  isPrintMode: z.boolean().optional(),
  xAxisLabel: z.string().optional(),
  viewConfiguration: ViewConfigurationSchema,
  isDisparities: z.boolean().optional(),
  groupLabels: z.record(z.string(), z.string()).optional(),
});

export type ProgressBarChartSerializableProps = z.infer<
  typeof ProgressBarChartSerializableProps
>;

export const PresentationBucket = z.object({
  values: z.array(LevelValue),
  key: z.string().optional(),
  year: z.string(),
  fittedValue: z.number().optional(),
  controlValue: z.number().optional(),
});

export type PresentationBucket = z.infer<typeof PresentationBucket>;

export const ProgressLineChartSerializableProps =
  ProgressBarChartSerializableProps.extend({
    data: PresentationBucket.array(),
    chartConfig: LineChartConfig,
    treatmentGroup: z.string().nullish(),
    maxXTicks: z.number().optional(),
    isBreakdown: z.boolean().optional(),
  });

export type ProgressLineChartSerializableProps = z.infer<
  typeof ProgressLineChartSerializableProps
>;

export const TimeSeriesLineChartSerializableProps = z.object({
  data: TimeSeriesTrendsResult,
  metricDefinition: MyMetricDefinition,
  units: NumericDisplayUnit,
  treatmentDate: z.string().nullish(),
  treatmentGroup: z.string().nullish(),
  viewBy: ViewByOption,
  isMetricBoolean: z.boolean(),
  isPrintMode: z.boolean(),
  config: LineChartConfig,
  viewConfiguration: ViewConfigurationSchema,
});

export type TimeSeriesLineChartSerializableProps = z.infer<
  typeof TimeSeriesLineChartSerializableProps
>;

export const MetricChangeConfidence = z.object({
  percentage: z.number(),
  sampleSize: z.number(),
});

export type MetricChangeConfidence = z.infer<typeof MetricChangeConfidence>;

export const MetricChangeProps = z.object({
  change: z.number(),
  direction: z.union([z.literal('up'), z.literal('down')]),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  confidence: MetricChangeConfidence,
  duration: DateDuration,
});

export type MetricChangeProps = z.infer<typeof MetricChangeProps>;

export const MetricChangeSectionSerializableProps = z.object({
  beforeJump: MetricChangeProps,
  jump: MetricChangeProps,
  afterJump: MetricChangeProps,
  metricDefinition: MyMetricDefinition,
  focusArea: FocusArea,
});

export type MetricChangeSectionSerializableProps = z.infer<
  typeof MetricChangeSectionSerializableProps
>;

// Treatment value logic
export const getTreatmentValue = (
  bucket: TimeSeriesTrendsResultBucket,
  element: TimeSeriesTrendsResultBucketElement,
  isEventStudy: boolean,
  isEventStudyDiD: boolean,
): number | null => {
  if (!isEventStudy) {
    return element.percentage;
  }

  if (isEventStudyDiD) {
    // If we have both treatmentDate and treatmentGroup, use controlValue
    return bucket.controlValue ?? null;
  } else {
    // If we have treatmentDate but no treatmentGroup, use treatmentValue (fittedValue)
    return bucket.fittedValue ?? null;
  }
};

export const createColorPaletteFromBuckets = (
  buckets: TimeSeriesTrendsResultBucket[],
): Record<string, Color> => {
  let lastUsedColorIndex = -1;
  const nonReservedColors: Color[] = Colors.filter(
    (color) => color !== 'LightGray' && color !== 'Raspberry',
  );
  const result: Record<string, Color> = {
    [AverageTrendSeriesId]: 'Raspberry',
    [ControlGroupSeriesId]: 'LightGray',
  };

  for (const bucket of buckets) {
    for (const element of bucket.elements) {
      const seriesId = buildLineGroupSeriesId({
        group: element.group,
      });
      if (result[seriesId]) {
        continue;
      }

      const colorIndex = lastUsedColorIndex === -1 ? 0 : lastUsedColorIndex + 1;
      const color =
        colorIndex >= nonReservedColors.length
          ? 'Gray'
          : nonReservedColors[colorIndex];

      lastUsedColorIndex = colorIndex;

      result[seriesId] = color;
    }
  }

  return result;
};

export const transformBuckets = ({
  buckets,
  isCheckered,
  treatmentDate,
  treatmentGroup,
}: {
  buckets: TimeSeriesTrendsResultBucket[] | null;
  isCheckered: boolean;
  treatmentDate?: string | null;
  treatmentGroup?: string | null;
}) => {
  if (!buckets) {
    return null;
  }

  let lastUsedColorIndex = -1;
  const colorsWithoutGray: Color[] = Colors.filter((color) => color !== 'Gray');

  return buckets.map(
    (item): PresentationBucket => ({
      year: humanizeDateRange({
        start: item.start,
        end: item.end,
      }),
      values: item.elements.map((v) => {
        const colorIndex =
          lastUsedColorIndex === -1 ? 0 : lastUsedColorIndex + 1;
        const color =
          colorIndex >= colorsWithoutGray.length
            ? 'Gray'
            : colorsWithoutGray[colorIndex];
        lastUsedColorIndex = colorIndex;

        return {
          level: v.group,
          value: getTreatmentValue(
            item,
            v,
            Boolean(treatmentDate),
            Boolean(treatmentGroup),
          ),
          color,
          count: v.total || 0,
          isCheckered: isCheckered,
          // Preserve observed value when doing event study analysis
          // For DiD: only for treatment group
          // For trend break: for all series
          observedValue:
            treatmentDate &&
            (treatmentGroup ? v.group === treatmentGroup : true)
              ? (v.percentage ?? undefined)
              : undefined,
          numerator: v.numerator,
        };
      }),
      key: `${item.start}-${item.end}`,
      fittedValue: item.fittedValue || undefined,
      controlValue: item.controlValue || undefined,
    }),
  );
};

export const ProgressComparisonBucketDetails = z.object({
  primary: LevelValue,
  comparison: LevelValue,
  groupComparison: z.string(),
});

export type ProgressComparisonBucketDetails = z.infer<
  typeof ProgressComparisonBucketDetails
>;

export const ProgressComparisonBucket = z.record(
  z.string(),
  ProgressComparisonBucketDetails,
);

export type ProgressComparisonBucket = z.infer<typeof ProgressComparisonBucket>;

export const ProgressPresentationData = z.object({
  data: z.array(PresentationBucket),
  primaryBucketToComparisonBucketData: ProgressComparisonBucket,
  isMetricBoolean: z.boolean(),
});

export type ProgressPresentationData = z.infer<typeof ProgressPresentationData>;

export const transformProgressDataForPresentation = ({
  data,
  treatmentDate,
  treatmentGroup,
}: {
  data: TimeSeriesTrendsResult | null;
  treatmentDate: string | null | undefined;
  treatmentGroup: string | null | undefined;
}): ProgressPresentationData => {
  if (!data) {
    return {
      data: [],
      primaryBucketToComparisonBucketData: {},
      isMetricBoolean: false,
    };
  }

  const isMetricBoolean = data.buckets.some((bucket) =>
    bucket.elements.some((el) => el.isMetricBoolean === true),
  );

  const primaryBuckets = transformBuckets({
    buckets: data.buckets,
    isCheckered: false,
    treatmentDate,
    treatmentGroup,
  });

  if (!primaryBuckets) {
    return {
      data: [],
      primaryBucketToComparisonBucketData: {},
      isMetricBoolean: isMetricBoolean,
    };
  }

  const comparisonBuckets = data.compareWithBuckets
    ? transformBuckets({
        buckets: data.compareWithBuckets,
        isCheckered: true,
        treatmentDate,
        treatmentGroup,
      })
    : null;

  if (!comparisonBuckets) {
    return {
      data: primaryBuckets,
      primaryBucketToComparisonBucketData: {},
      isMetricBoolean: isMetricBoolean,
    };
  }

  const primaryLevelComparisonData: ProgressComparisonBucket = {};
  const bucks: PresentationBucket[] = [];

  zip(primaryBuckets, comparisonBuckets).forEach((x) => {
    if (!x) return;
    const [primary, comparison] = x;
    if (!primary || !comparison) return;

    const values: LevelValue[] = [];
    for (const primaryValue of primary.values) {
      const comparisonValue = comparison.values.find(
        (v) => v.level === primaryValue.level,
      );
      values.push(primaryValue);
      if (comparisonValue) {
        primaryLevelComparisonData[`${primary.year}_${primaryValue.level}`] = {
          primary: primaryValue,
          comparison: comparisonValue,
          groupComparison: comparison.year,
        };
        values.push(comparisonValue);
      }
    }

    bucks.push({
      year: primary.year,
      values: values,
      key: primary.year,
    });
  });

  return {
    data: bucks,
    primaryBucketToComparisonBucketData: primaryLevelComparisonData,
    isMetricBoolean: isMetricBoolean,
  };
};

const shouldEnableBreakdownLegend = ({
  isBreakdown,
  treatmentDate,
  treatmentGroup,
}: {
  isBreakdown: boolean;
  treatmentDate: string | null | undefined;
  treatmentGroup: string | null | undefined;
}) => {
  if (!isBreakdown) {
    return false;
  }

  if (treatmentDate && treatmentGroup) {
    return false;
  }

  return true;
};

export const createProgressLineChartConfig = ({
  graphData,
  treatmentDate,
  treatmentGroup,
  isBreakdown,
}: {
  graphData: TimeSeriesTrendsResult | null;
  treatmentDate: string | null | undefined;
  treatmentGroup: string | null | undefined;
  isBreakdown: boolean;
}) => {
  const config = createDefaultLineChartConfig();

  // Configure legend as buttons (existing behavior)
  config.legend = {
    enabled: shouldEnableBreakdownLegend({
      isBreakdown,
      treatmentDate,
      treatmentGroup,
    }),
    position: 'bottom',
    justify: true,
    style: 'buttons',
    orientation: 'horizontal',
  };

  // Add treatment marker only if treatment analysis has been performed
  // (i.e., the data has fittedValue or controlValue, which means Apply was clicked)

  // Check if treatment analysis data exists in the graph data
  const hasTreatmentAnalysis = graphData?.buckets.some(
    (bucket) => bucket.fittedValue != null || bucket.controlValue != null,
  );

  if (
    treatmentDate &&
    treatmentDate !== EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE &&
    graphData &&
    hasTreatmentAnalysis
  ) {
    // Find the bucket that contains the treatment date
    const treatmentBucket = graphData.buckets.find((bucket) => {
      const bucketStart = parseLocalDate(bucket.start);
      const bucketEnd = parseLocalDate(bucket.end);
      const treatment = parseLocalDate(treatmentDate);
      return treatment >= bucketStart && treatment <= bucketEnd;
    });

    if (treatmentBucket) {
      // Format the actual treatment date for display
      const treatmentDateObj = parseLocalDate(treatmentDate);
      const formattedTreatmentDate = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(treatmentDateObj);

      // Always add the vertical treatment line with gradient area
      config.markers = [
        {
          id: 'treatment-line',
          axis: 'x',
          value: treatmentBucket.start,
          color: '#F07B54',
          strokeWidth: 2,
          strokeOpacity: 0,
          label: formattedTreatmentDate,
          labelOrientation: 'vertical',
          labelPosition: 'top',
          gradientArea: {
            enabled: true,
            opacity: 0.15,
          },
        },
      ];

      // Add arrow marker for both DiD analysis and trend break scenarios
      if (treatmentGroup) {
        // DiD analysis: arrow from control group to treatment group
        // Find the first control group (any group that's not the treatment group)
        config.markers.push({
          id: 'treatment-arrow',
          axis: 'x',
          value: treatmentBucket.start,
          color: '#F07B54',
          strokeWidth: 4,
          arrow: {
            enabled: true,
            fromSeries: ControlGroupSeriesId, // Control series (synthetic control)
            toSeries: buildLineGroupSeriesId({ group: treatmentGroup }), // Treatment series (actual treated group) //TODO NO LONGER RIGHT
            headSize: 6,
          },
        });

        // Configure styling for treatment group series
        // Fitted line: dashed, emphasized with full opacity, wider stroke
        config.series[treatmentGroup] = {
          style: 'dashed',
          width: 2.5,
          opacity: 1,
          points: {
            enabled: false,
          },
          color: ConfigColors.Raspberry.Primary.raw,
        };

        // Observed data line: solid, lighter to de-emphasize
        config.series[`${treatmentGroup} (Observed)`] = {
          style: 'solid',
          width: 2.5,
          opacity: 0.5,
        };
      } else {
        // Trend break analysis: arrow from x-axis to treatment line
        // Use the first available series (will be handled by TreatmentArrowLayer)
        const firstSeries = graphData.buckets[0]?.elements[0]?.group;
        if (firstSeries) {
          config.markers.push({
            id: 'treatment-arrow',
            axis: 'x',
            value: treatmentBucket.start,
            color: '#F07B54',
            strokeWidth: 4,
            arrow: {
              enabled: true,
              fromSeries: AverageTrendSeriesId, // Single series for trend break
              toSeries: AverageTrendSeriesId, // Same series (indicates trend break)
              headSize: 6,
            },
          });
        }

        // Configure styling for trend break
        // One overall fitted line + observed lines for each group

        // Fitted trend line: dashed, emphasized with full opacity, wider stroke
        config.series['Fitted Trend'] = {
          style: 'dashed',
          width: 2.5,
          opacity: 1,
          color: ConfigColors.Raspberry.Primary.raw,
          points: {
            enabled: false,
          },
        };

        // Observed data lines: solid, lighter to de-emphasize, using Primary color
        graphData.buckets[0]?.elements.forEach((element) => {
          const seriesName = element.group;

          config.series[`${seriesName} (Observed)`] = {
            style: 'solid',
            width: 2.5,
            opacity: 0.2,
            // Primary color will be applied automatically by LineChart component
          };
        });
      }

      // Adjust line width when treatment marker is present
      config.defaultLineStyle.width = 2.5;
    }

    config.showTreatmentGradient =
      graphData.jump != null && graphData.jump !== 0;
  }

  return config;
};

export const EVENT_STUDY_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export const EVENT_STUDY_DATE_FORMAT_PATTERN = getStandardDateFormatPattern(
  EVENT_STUDY_DATE_FORMATTER,
);

export const EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE =
  EVENT_STUDY_DATE_FORMAT_PATTERN.toLocaleLowerCase();

/**
 * Calculate total sample size from buckets
 */
function calculateSampleSize(
  buckets: TimeSeriesTrendsResult['buckets'],
): number {
  return buckets.reduce((sum, bucket) => {
    const bucketTotal = bucket.elements[0]?.total || 0;
    return sum + bucketTotal;
  }, 0);
}

/**
 * Transform p-value to confidence percentage
 */
function pValueToConfidence(pValue: number): number {
  return Math.round((1 - pValue) * 100);
}

/**
 * Transforms Event Study Analysis results from backend to MetricChangeSectionSerializableProps
 *
 * @param result - TimeSeriesTrendsResult from backend
 * @param treatmentDate - Treatment date in YYYY-MM-DD format
 */

export function transformEventStudyDataToMetricChanges(
  result: TimeSeriesTrendsResult,
  treatmentDate: string,
): Omit<
  MetricChangeSectionSerializableProps,
  'metricDefinition' | 'focusArea'
> | null {
  if (
    result.beforeJump == null ||
    result.jump == null ||
    result.afterJump == null
  ) {
    return null;
  }

  const sortedBuckets = [...result.buckets].sort((a, b) => {
    const aStart = parseISO(a.start);
    const bStart = parseISO(b.start);

    const cmp = compareAsc(aStart, bStart);

    if (cmp !== 0) return cmp;

    const aEnd = parseISO(a.end);
    const bEnd = parseISO(b.end);
    return compareAsc(aEnd, bEnd);
  });

  const treatmentDateParsed = parseISO(treatmentDate);

  // Find treatment bucket index
  const treatmentBucketIndex = sortedBuckets.findIndex((b) =>
    isWithinInterval(treatmentDateParsed, {
      start: parseISO(b.start),
      end: parseISO(b.end),
    }),
  );

  if (treatmentBucketIndex === -1) {
    return null;
  }

  // Split buckets into before/treatment/after
  const preTreatmentBuckets = sortedBuckets.slice(0, treatmentBucketIndex);
  const treatmentBucket = sortedBuckets[treatmentBucketIndex];
  const postTreatmentBuckets = sortedBuckets.slice(treatmentBucketIndex + 1);

  const jumpStartDate = parseISO(treatmentBucket.start);
  const jumpEndDate = parseISO(treatmentBucket.end);

  const beforeJumpStartDate = parseISO(preTreatmentBuckets[0].start);
  const beforeJumpEndDate = parseISO(
    preTreatmentBuckets[preTreatmentBuckets.length - 1].end,
  );
  const afterJumpStartDate = parseISO(postTreatmentBuckets[0].start);
  const afterJumpEndDate = parseISO(
    postTreatmentBuckets[postTreatmentBuckets.length - 1].end,
  );

  // Calculate sample sizes
  const beforeSampleSize = calculateSampleSize(preTreatmentBuckets);
  const jumpSampleSize = treatmentBucket?.elements[0]?.total || 0;
  const afterSampleSize = calculateSampleSize(postTreatmentBuckets);

  return {
    beforeJump: {
      change: result.beforeJump,
      direction: result.beforeJump >= 0 ? 'up' : 'down',
      startDate: beforeJumpStartDate,
      endDate: beforeJumpEndDate,
      confidence: {
        percentage: pValueToConfidence(result.beforeJumpConfidence || 0),
        sampleSize: beforeSampleSize,
      },
      duration: getDateDuration({
        start: beforeJumpStartDate,
        end: beforeJumpEndDate,
      }),
    },
    jump: {
      change: result.jump,
      direction: result.jump >= 0 ? 'up' : 'down',
      startDate: jumpStartDate,
      endDate: jumpEndDate,
      confidence: {
        percentage: pValueToConfidence(result.jumpConfidence || 0),
        sampleSize: jumpSampleSize,
      },
      duration: getDateDuration({ start: jumpStartDate, end: jumpEndDate }),
    },
    afterJump: {
      change: result.afterJump,
      direction: result.afterJump >= 0 ? 'up' : 'down',
      startDate: afterJumpStartDate,
      endDate: afterJumpEndDate,
      confidence: {
        percentage: pValueToConfidence(result.afterJumpConfidence || 0),
        sampleSize: afterSampleSize,
      },
      duration: getDateDuration({
        start: afterJumpStartDate,
        end: afterJumpEndDate,
      }),
    },
  };
}

export const AverageTrendSeriesId = 'averageTrend';
export const ControlGroupSeriesId = 'controlGroup';
export const ComparedControlGroupSeriesId = 'comparedControlGroup';

const groupIdPrefix = 'group-';

export const buildLineGroupSeriesId = ({ group }: { group: string }) =>
  `${groupIdPrefix}${group}`;

export const isLineGroupSeriesId = (id: string) => id.startsWith(groupIdPrefix);

export const extractLineGroupFromSeriesId = (id: string): string | null => {
  return isLineGroupSeriesId(id) ? id.slice(groupIdPrefix.length) : null;
};

export const ObservedMetricLineGroup = 'All';

export const createGroupLabelsMapForComparisonMode = ({
  primaryBucketToComparisonBucketData,
}: {
  primaryBucketToComparisonBucketData: ProgressComparisonBucket;
}): Record<string, string> => {
  const labels: Record<string, string> = {};

  // Build a map of group IDs to their comparison period labels
  // e.g., {'2020': '2020 v. 2013'}
  Object.entries(primaryBucketToComparisonBucketData).forEach(
    ([key, comparisonData]) => {
      // Extract the group ID from the key (format: "groupId_level")
      const groupId = key.split('_')[0];

      // Create the label with comparison period if we haven't already
      if (!labels[groupId] && comparisonData.groupComparison) {
        labels[groupId] = `${groupId} v. ${comparisonData.groupComparison}`;
      }
    },
  );

  return labels;
};
